import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getCandidateFeedbackStatus,
  submitCandidateFeedback,
} from "../services/feedback.service";
import {
  CandidateFeedback,
  CandidateFeedbackFormData,
  CandidateFeedbackStatusResponse,
} from "../models/feedback";

export const candidateFeedbackKeys = {
  status: (token?: string) => ["candidate-feedback", token] as const,
};

interface UseCandidateFeedbackResult {
  status?: CandidateFeedbackStatusResponse;
  isLoading: boolean;
  error?: string;
  submitFeedback: (payload: CandidateFeedbackFormData) => Promise<CandidateFeedback>;
  isSubmitting: boolean;
}

export function useCandidateFeedback(
  invitationToken?: string
): UseCandidateFeedbackResult {
  const queryClient = useQueryClient();

  const statusQuery = useQuery({
    queryKey: candidateFeedbackKeys.status(invitationToken),
    queryFn: async () => {
      if (!invitationToken) {
        throw new Error("Invitation token is required");
      }
      const response = await getCandidateFeedbackStatus(invitationToken);
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data as CandidateFeedbackStatusResponse;
    },
    enabled: Boolean(invitationToken),
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: async (payload: CandidateFeedbackFormData) => {
      if (!invitationToken) {
        throw new Error("Invitation token missing");
      }
      const response = await submitCandidateFeedback(invitationToken, payload);
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data as CandidateFeedback;
    },
    onSuccess: (data) => {
      if (!invitationToken) {
        return;
      }
      queryClient.setQueryData(
        candidateFeedbackKeys.status(invitationToken),
        {
          interview_id: data.interview_id,
          has_submitted: true,
          feedback: data,
        } satisfies CandidateFeedbackStatusResponse
      );
    },
  });

  return {
    status: statusQuery.data,
    isLoading: statusQuery.isLoading,
    error: statusQuery.error
      ? (statusQuery.error as Error).message
      : undefined,
    submitFeedback: mutation.mutateAsync,
    isSubmitting: mutation.isPending,
  };
}

